import React, { useRef, useEffect, useState } from "react";
import { useUpload } from "../../context/UploadContext";
import { formatFileSize } from "../../utils/helpers";
import { encoderLabel } from "../../services/videoCompressionService";

const statusIcon = (status, fileType) => {
  switch (status) {
    case "checking": return "🔍";
    case "compressing": return "⚙️";
    case "uploading": return "📡";
    case "retrying": return "🔁";
    case "done": return "✅";
    case "failed": return "❌";
    default: return fileType?.startsWith("video/") ? "🎬" : "🖼️";
  }
};

const formatEta = (seconds) => {
  if (seconds == null) return "";
  if (seconds < 5) return " — a few seconds left";
  if (seconds < 60) return ` — ${seconds}s left`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return secs > 0 ? ` — ${mins}m ${secs}s left` : ` — ${mins}m left`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? ` — ${hrs}h ${remMins}m left` : ` — ${hrs}h left`;
};

const formatMbps = (mbps) => {
  const n = Number(mbps || 0);
  if (!isFinite(n) || n <= 0) return "";
  return ` @ ${n.toFixed(n >= 100 ? 0 : 2)} Mbps`;
};

const compressionStatusText = (item) => {
  const pct = Math.max(0, Math.min(100, Number(item.compressProgress || 0)));
  if (item.fileType?.startsWith("video/") && pct <= 30) {
    return `Copying to Studio ${pct}%`;
  }
  return `Compressing ${pct}%`;
};

const statusText = (item) => {
  switch (item.status) {
    case "checking": return "Analysing...";
    case "compressing": return compressionStatusText(item);
    case "uploading":
      if (item.uploadPhase === "handoff") {
        return `Preparing upload ${item.uploadProgress}%${formatMbps(item.uploadMbps)}`;
      }
      return `Uploading ${item.uploadProgress}%${formatMbps(item.uploadMbps)}${formatEta(item.eta)}`;
    case "retrying": return "Waiting for network — will retry...";
    case "done": return "Done";
    case "failed": return item.error || "Failed";
    default: return "Queued";
  }
};

const RunningUploads = () => {
  const { sessions, removeSession, cancelSession } = useUpload();
  const [confirmId, setConfirmId] = React.useState(null);
  const [expandedIds, setExpandedIds] = useState({}); // { [sessionId]: true }
  const fileListRefs = useRef({});

  const toggleExpand = (id) => setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));

  // Auto-scroll each session's file list to the active item
  useEffect(() => {
    Object.values(sessions).forEach((session) => {
      if (!expandedIds[session.id]) return;
      const container = fileListRefs.current[session.id];
      if (!container || !session.started) return;
      const activeItem = (session.fileItems || []).find((i) =>
        ["checking", "compressing", "uploading", "retrying"].includes(i.status)
      );
      if (!activeItem) return;
      const el = container.querySelector(`[data-file-id="${activeItem.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, [sessions, expandedIds]);
  const sessionList = Object.values(sessions).reverse();

  if (sessionList.length === 0) {
    return (
      <div style={styles.empty}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>📤</div>
        <h3 style={{ margin: "0 0 6px", color: "#666" }}>No active uploads</h3>
        <p style={{ margin: 0, color: "#999", fontSize: 13 }}>
          When you start uploading files, they will appear here.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 style={styles.pageTitle}>Running Uploads</h2>
      {sessionList.map((session) => {
        const items = session.fileItems || [];
        const doneCount = items.filter((i) => i.status === "done").length;
        const failCount = items.filter((i) => i.status === "failed").length;
        const totalCount = items.length;
        const allDone = totalCount > 0 && items.every((i) => i.status === "done" || i.status === "failed");
        const isActive = session.started && items.some((i) => ["pending", "checking", "compressing", "uploading", "retrying"].includes(i.status));
        const overallProgress = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
        const activeItem = items.find((i) => ["checking", "compressing", "uploading", "retrying"].includes(i.status));

        return (
          <div key={session.id} style={styles.sessionCard}>
            {/* Cancel confirm dialog */}
            {confirmId === session.id && (
              <div style={styles.confirmOverlay}>
                <div style={styles.confirmBox}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Cancel Upload?</div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 16 }}>
                    All pending files in this session will be cancelled.
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={styles.confirmKeepBtn} onClick={() => setConfirmId(null)}>Keep Uploading</button>
                    <button style={styles.confirmCancelBtn} onClick={() => { cancelSession(session.id); setConfirmId(null); }}>Yes, Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Compact Header Row */}
            <div style={styles.sessionHeader}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.sessionTitle}>
                  {session.gallery?.Name} / {session.album?.Name}
                </div>
                <div style={styles.sessionMeta}>
                  {doneCount}/{totalCount} files
                  {failCount > 0 && <span style={{ color: "#e53935" }}> • {failCount} failed</span>}
                  {allDone && <span style={{ color: "#16a34a", fontWeight: 600 }}> • Complete</span>}
                  {isActive && !allDone && <span style={{ color: "#6c63ff", fontWeight: 600 }}> • Uploading</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {isActive && (
                  <button style={styles.cancelBtn} onClick={() => setConfirmId(session.id)}>
                    ✕ Cancel
                  </button>
                )}
                {allDone && (
                  <button style={styles.dismissBtn} onClick={() => removeSession(session.id)}>
                    Dismiss
                  </button>
                )}
              </div>
            </div>

            {/* Overall progress bar */}
            <div style={styles.overallBarWrap}>
              <div style={{
                ...styles.overallBarFill,
                width: `${overallProgress}%`,
                background: allDone ? "#16a34a" : "#6c63ff",
              }} />
            </div>

            {/* Current file mini-status (shown when collapsed) */}
            {activeItem && !expandedIds[session.id] && (
              <div style={styles.activeFileMini}>
                <span style={{ fontSize: 13 }}>{statusIcon(activeItem.status, activeItem.fileType)}</span>
                <span style={styles.activeFileName}>{activeItem.fileName}</span>
                <span style={styles.activeFileStatus}>{statusText(activeItem)}</span>
              </div>
            )}

            {/* Toggle details button */}
            <button
              style={styles.toggleBtn}
              onClick={() => toggleExpand(session.id)}
            >
              {expandedIds[session.id] ? "▲ Hide Details" : `▼ Show Details (${totalCount} files)`}
            </button>

            {/* Collapsible File list */}
            {expandedIds[session.id] && (
              <div style={styles.fileList} ref={(el) => { fileListRefs.current[session.id] = el; }}>
                {items.map((item) => (
                  <div key={item.id} data-file-id={item.id} style={{
                    ...styles.fileRow,
                    background: item.status === "done" ? "#f0fdf4" : item.status === "failed" ? "#fef2f2" : "#fff",
                  }}>
                    <span style={styles.fileIcon}>{statusIcon(item.status, item.fileType)}</span>
                    <div style={styles.fileInfo}>
                      <span style={styles.fileName}>{item.fileName}</span>

                      {item.status === "compressing" && (
                        <div style={styles.barWrap}>
                          <div style={{ ...styles.barFill, width: `${item.compressProgress}%`, background: "#f59e0b" }} />
                        </div>
                      )}
                      {item.status === "uploading" && (
                        <div style={styles.barWrap}>
                          <div style={{ ...styles.barFill, width: `${item.uploadProgress}%`, background: "#6c63ff" }} />
                        </div>
                      )}

                      <span style={{
                        ...styles.statusLabel,
                        color: item.status === "done" ? "#16a34a" : item.status === "failed" ? "#e53935" : "#888",
                      }}>
                        {statusText(item)}
                        {item.status === "pending" && item.queuePosition > 0 && (
                          <span style={{ color: "#aaa" }}> (#{item.queuePosition} in queue)</span>
                        )}
                      </span>
                                          {item.compressEngine && (
                                            <span style={{
                                              display: "inline-block",
                                              marginTop: 2,
                                              fontSize: 10,
                                              fontWeight: 600,
                                              color: item.compressEngine === "libx264" ? "#888" : "#6c63ff",
                                              background: item.compressEngine === "libx264" ? "#f3f4f6" : "#ede9fe",
                                              borderRadius: 4,
                                              padding: "1px 6px",
                                            }}>
                                              {encoderLabel(item.compressEngine)}
                                            </span>
                                          )}
                    </div>
                    <span style={item.originalSize ? styles.fileSizeCompressed : styles.fileSize}>
                      {item.originalSize
                        ? <>{formatFileSize(item.originalSize)}<span style={styles.sizeArrow}> → </span><span style={styles.sizeAfter}>{formatFileSize(item.fileSize)}</span>{item.compressSec && <span style={styles.compressTime}> ({item.compressSec}s)</span>}</>
                        : formatFileSize(item.fileSize)
                      }
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const styles = {
  pageTitle: {
    margin: "0 0 20px",
    fontSize: 22,
    fontWeight: 700,
    color: "#1e1e2d",
  },
  empty: {
    textAlign: "center",
    padding: "80px 20px",
  },
  sessionCard: {
    background: "#fff",
    borderRadius: 14,
    padding: "16px 20px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    marginBottom: 16,
    position: "relative",
    overflow: "hidden",
  },
  sessionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  sessionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#1e1e2d",
    marginBottom: 2,
  },
  sessionMeta: {
    fontSize: 11,
    color: "#888",
  },
  dismissBtn: {
    background: "none",
    border: "1px solid #ddd",
    borderRadius: 6,
    padding: "4px 12px",
    fontSize: 12,
    color: "#888",
    cursor: "pointer",
  },
  cancelBtn: {
    background: "none",
    border: "1px solid #e53935",
    borderRadius: 6,
    padding: "4px 12px",
    fontSize: 12,
    color: "#e53935",
    cursor: "pointer",
    fontWeight: 600,
  },
  confirmOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 5,
    background: "rgba(0,0,0,0.3)",
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  confirmBox: {
    background: "#fff",
    borderRadius: 12,
    padding: "24px 20px",
    width: "80%",
    maxWidth: 300,
    textAlign: "center",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  },
  confirmKeepBtn: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: 6,
    border: "1px solid #ddd",
    background: "#fff",
    fontSize: 12,
    cursor: "pointer",
    color: "#333",
  },
  confirmCancelBtn: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: 6,
    border: "none",
    background: "#e53935",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  overallBarWrap: {
    height: 5,
    background: "#f0f0f0",
    borderRadius: 3,
    overflow: "hidden",
    marginBottom: 10,
  },
  overallBarFill: {
    height: "100%",
    borderRadius: 3,
    transition: "width 0.3s ease",
  },
  fileList: {
    maxHeight: 320,
    overflowY: "auto",
    border: "1px solid #f0f0f0",
    borderRadius: 8,
    marginTop: 8,
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderBottom: "1px solid #f5f5f5",
    fontSize: 12,
  },
  fileIcon: { fontSize: 13, flexShrink: 0 },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: {
    display: "block",
    color: "#333",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: 2,
  },
  barWrap: {
    height: 4,
    background: "#f0f0f0",
    borderRadius: 2,
    overflow: "hidden",
    marginTop: 4,
    marginBottom: 2,
  },
  barFill: {
    height: "100%",
    borderRadius: 2,
    transition: "width 0.2s ease",
  },
  statusLabel: {
    fontSize: 11,
    color: "#888",
    display: "block",
    marginTop: 2,
  },
  fileSize: {
    color: "#aaa",
    flexShrink: 0,
    fontSize: 11,
    minWidth: 44,
    textAlign: "right",
  },
  fileSizeCompressed: {
    flexShrink: 0,
    fontSize: 11,
    minWidth: 44,
    textAlign: "right",
    color: "#aaa",
  },
  sizeArrow: {
    color: "#bbb",
  },
  sizeAfter: {
    color: "#16a34a",
    fontWeight: 700,
  },
  compressTime: {
    color: "#9ca3af",
    fontWeight: 400,
  },
  activeFileMini: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    background: "#f9f8ff",
    borderRadius: 6,
    marginBottom: 6,
    overflow: "hidden",
  },
  activeFileName: {
    fontSize: 12,
    fontWeight: 600,
    color: "#333",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
    minWidth: 0,
  },
  activeFileStatus: {
    fontSize: 11,
    color: "#6c63ff",
    fontWeight: 600,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  toggleBtn: {
    background: "#f5f3ff",
    border: "1px solid #e0daf7",
    fontSize: 12,
    color: "#6c63ff",
    cursor: "pointer",
    padding: "6px 14px",
    fontWeight: 600,
    width: "100%",
    textAlign: "center",
    borderRadius: 6,
    marginTop: 4,
  },
};

export default RunningUploads;
